import React, { useState } from "react"
import Navbar from "@/Components/Navbar"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import { mapDesigns } from "@mono/mapDesigns"
import { host } from "@/utils/utils"

// Type definitions
interface IPlayer {
  money: number
  savedFromRent?: boolean
  properties: number[]
  pieceId: string
  position: number
  eliminated: boolean
  rollHistory: [number, number][]
  id: string
  order: number
  inJail: boolean
  inJailTurns: number
  jailFreeCards: string[]
  positionAfterAnimation?: number
  auctionData?: any
  disconnected?: boolean
  turnTimeRemainingWhenDisconnected?: number
  turnTimeLeft?: number
  isBankrupt: boolean
  boughtPropertiesWithoutAuction: number
  canBuyPropertiesWithoutAuction: number
  cards: any[]
  stock: number
  role: string
  stockMultiplier: number
  turnsBeingBankrupt: number
  bot?: boolean
}

type PlayerOrder = number
type GameRoleId = string

interface PropertyData {
  order: number
  name: string
  price: number
  color?: string
  groupId?: string
}

interface TradeSimulatorProps {}

// Map used by the trade simulator. Persisted state is scoped to this id so that
// switching maps doesn't restore a stale bank/players from a different map.
const MAP_ID = "1"

// Drop target identifier: "bank" or a player's index in the `players` array.
type DropTarget = "bank" | number

interface ITradeSimTrade {
  offeringPlayerOrder: number
  offeredPlayerOrder: number
  givenProperties: number[]
  takenProperties: number[]
  moneyGiven: number
  moneyTaken: number
}

// Mirrors ITradeValuation returned by /api/trade/check-trade (bot/trade.ts).
interface TradePropertyValuation {
  propertyOrder: number
  intrinsicValue: number
  setPremium: number
  houseValue: number
  threatValue: number
  totalValue: number
  completesSet: boolean
  breaksCompletedSet: boolean
}

interface TradeValuation {
  accept: boolean
  netValue: number
  valueReceived: number
  valueGiven: number
  requiredSurplus: number
  moneyReceived: number
  moneyReceivedUtility: number
  moneyGiven: number
  cashAfterTrade: number
  safetyReserve: number
  rejectedByGuard?: string
  receivedProperties: TradePropertyValuation[]
  givenProperties: TradePropertyValuation[]
}

const PLAYER_ACCENTS = [
  "#f87171", // red
  "#60a5fa", // blue
  "#4ade80", // green
  "#facc15", // yellow
  "#c084fc", // purple
  "#fb923c", // orange
]

const ROLE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "auction-strategist", label: "Auction Strategist" },
  { value: "card-stealer", label: "Card Stealer" },
  { value: "rent-lord", label: "Rent Lord" },
  { value: "wall-street-wolf", label: "Wall Street Wolf" },
  { value: "private-buyer", label: "Private Buyer" },
  { value: "financer", label: "Financer" },
  { value: "the-constructor", label: "The Constructor" },
]

// Property Small Card Component
const PropertySmallCard: React.FC<{
  property: PropertyData
  isInBank?: boolean
  onDragStart: (event: React.DragEvent, property: PropertyData) => void
  onDragEnd: () => void
  isDragging?: boolean
}> = ({ property, isInBank, onDragStart, onDragEnd, isDragging }) => {
  return (
    <div
      draggable
      onDragStart={(event) => onDragStart(event, property)}
      onDragEnd={onDragEnd}
      className={`
        group relative border h-11 w-9 border-black/60 cursor-grab active:cursor-grabbing shadow-lg small-card
        transition-transform duration-150 select-none will-change-transform
        ${isDragging ? "opacity-30 scale-95" : "opacity-100"}
        ${isInBank ? "opacity-80" : ""}
        bg-white hover:scale-110
      `}
      title={`${property.name} ($${property.price})`}
      data-property-order={property.order}
    >
      {property.color && (
        <div
          className="h-3 w-full border-b border-black/60"
          style={{ backgroundColor: property.color }}
        ></div>
      )}
      <div className="w-full h-full p-[2px] py-[3px]">
        <div className="border-black h-full w-full flex items-center justify-center">
          <div className="text-xs font-bold text-black leading-none">
            {property.order}
          </div>
        </div>
      </div>
      {/* Tooltip on hover */}
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover:block whitespace-nowrap bg-black/90 text-white text-[10px] rounded px-2 py-1 z-10 shadow-lg">
        {property.name} — ${property.price}
      </div>
    </div>
  )
}

// Property card used by the Trade Check panel: click to include/exclude the
// property from the hypothetical trade instead of dragging it.
const SelectablePropertyCard: React.FC<{
  property: PropertyData
  checked: boolean
  onToggle: (order: number) => void
}> = ({ property, checked, onToggle }) => {
  return (
    <button
      type="button"
      onClick={() => onToggle(property.order)}
      className={`
        group relative border h-11 w-9 cursor-pointer shadow-lg small-card select-none
        transition-transform duration-150 bg-white
        ${
          checked
            ? "border-emerald-400 ring-2 ring-emerald-400 scale-105"
            : "border-black/60 opacity-60 hover:opacity-100"
        }
      `}
      title={`${property.name} ($${property.price})`}
    >
      {property.color && (
        <div
          className="h-3 w-full border-b border-black/60"
          style={{ backgroundColor: property.color }}
        ></div>
      )}
      <div className="w-full h-full p-[2px] py-[3px]">
        <div className="border-black h-full w-full flex items-center justify-center">
          <div className="text-xs font-bold text-black leading-none">
            {property.order}
          </div>
        </div>
      </div>
      {checked && (
        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 text-white text-[10px] leading-none flex items-center justify-center shadow">
          ✓
        </div>
      )}
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover:block whitespace-nowrap bg-black/90 text-white text-[10px] rounded px-2 py-1 z-10 shadow-lg">
        {property.name} — ${property.price}
      </div>
    </button>
  )
}

// Drop zone wrapper shared by the bank panel and each player's properties panel
const PropertyDropZone: React.FC<{
  target: DropTarget
  isActive: boolean
  onDragEnter: (target: DropTarget) => void
  onDragLeave: () => void
  onDrop: (target: DropTarget, event: React.DragEvent) => void
  children: React.ReactNode
  isEmpty: boolean
  emptyLabel: string
  // "rows" expects `children` to already be pre-grouped row elements (one
  // row per color group) instead of a flat, freely-wrapping list of cards.
  layout?: "wrap" | "rows"
}> = ({
  target,
  isActive,
  onDragEnter,
  onDragLeave,
  onDrop,
  children,
  isEmpty,
  emptyLabel,
  layout = "wrap",
}) => {
  return (
    <div
      onDragOver={(event) => event.preventDefault()}
      onDragEnter={(event) => {
        event.preventDefault()
        onDragEnter(target)
      }}
      onDragLeave={onDragLeave}
      onDrop={(event) => {
        event.preventDefault()
        onDrop(target, event)
      }}
      className={`
        min-h-9 rounded-lg p-1.5 border-2 border-dashed transition-colors duration-150
        ${
          isActive
            ? "border-emerald-400 bg-emerald-400/10"
            : "border-white/30 bg-white/5"
        }
      `}
    >
      <div
        className={layout === "rows" ? "flex flex-col gap-1.5" : "flex flex-wrap gap-1"}
      >
        {children}
      </div>
      {isEmpty && (
        <div className="text-white/40 text-center py-1 text-[11px]">
          {emptyLabel}
        </div>
      )}
    </div>
  )
}

// Compact player configuration card
const PlayerCard: React.FC<{
  player: IPlayer
  index: number
  accent: string
  playerProperties: PropertyData[]
  dragOverTarget: DropTarget | null
  onDragEnter: (target: DropTarget) => void
  onDragLeave: () => void
  onDrop: (target: DropTarget, event: React.DragEvent) => void
  onPropertyDragStart: (event: React.DragEvent, property: PropertyData) => void
  onPropertyDragEnd: () => void
  draggingOrder: number | null
  updatePlayerMoney: (playerIndex: number, money: number) => void
  updatePlayerField: (playerIndex: number, field: keyof IPlayer, value: any) => void
  removeAllPlayerProperties: (playerIndex: number) => void
  onCreateOffer: (playerIndex: number) => void
}> = ({
  player,
  index,
  accent,
  playerProperties,
  dragOverTarget,
  onDragEnter,
  onDragLeave,
  onDrop,
  onPropertyDragStart,
  onPropertyDragEnd,
  draggingOrder,
  updatePlayerMoney,
  updatePlayerField,
  removeAllPlayerProperties,
  onCreateOffer,
}) => {
  return (
    <div
      className="bg-slate-900/60 rounded-lg p-4 border border-white/10 shadow-lg"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow shrink-0"
            style={{ backgroundColor: accent }}
          >
            {index + 1}
          </div>
          <h3 className="text-base font-semibold text-white leading-tight">
            Player {index + 1}
          </h3>
        </div>
        <div className="text-right leading-tight">
          <div className="text-white text-base font-semibold">
            ${player.money.toLocaleString()}
          </div>
          <div className="text-white/40 text-xs">
            +${playerProperties.reduce((s, p) => s + p.price, 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-2.5 grid grid-cols-4 gap-1.5">
        <input
          type="number"
          placeholder="Money"
          min="0"
          max="5000"
          value={player.money}
          onChange={(e) => updatePlayerMoney(index, Number(e.target.value))}
          className="w-full bg-white/20 text-white text-sm border border-white/30 rounded px-2 py-1.5 focus:outline-none focus:border-white placeholder-white/50"
        />
        <select
          value={player.role}
          onChange={(e) => updatePlayerField(index, "role", e.target.value)}
          className="w-full bg-white/20 text-white text-sm border border-white/30 rounded px-1.5 py-1.5 focus:outline-none focus:border-white"
        >
          {ROLE_OPTIONS.map((role) => (
            <option
              key={role.value}
              value={role.value}
              className="bg-gray-800 text-white"
            >
              {role.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Stock"
          min="0"
          max="1000"
          value={player.stock}
          onChange={(e) => updatePlayerField(index, "stock", Number(e.target.value))}
          className="w-full bg-white/20 text-white text-sm border border-white/30 rounded px-2 py-1.5 focus:outline-none focus:border-white placeholder-white/50"
        />
        <input
          type="number"
          placeholder="Mult"
          min="0.1"
          max="10"
          step="0.1"
          value={player.stockMultiplier}
          onChange={(e) =>
            updatePlayerField(index, "stockMultiplier", Number(e.target.value))
          }
          className="w-full bg-white/20 text-white text-sm border border-white/30 rounded px-2 py-1.5 focus:outline-none focus:border-white placeholder-white/50"
        />
      </div>

      {/* Properties */}
      <div className="mb-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-white/70 text-xs font-medium">
            Properties ({playerProperties.length})
          </label>
          <button
            onClick={() => removeAllPlayerProperties(index)}
            disabled={playerProperties.length === 0}
            className="text-xs px-2 py-1 rounded font-medium transition-colors bg-red-600/80 hover:bg-red-600 text-white disabled:bg-white/10 disabled:text-white/30 disabled:cursor-not-allowed"
          >
            Remove all
          </button>
        </div>
        <PropertyDropZone
          target={index}
          isActive={dragOverTarget === index}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          isEmpty={playerProperties.length === 0}
          emptyLabel="Drag properties here"
        >
          {playerProperties.map((property) => (
            <PropertySmallCard
              key={property.order}
              property={property}
              onDragStart={onPropertyDragStart}
              onDragEnd={onPropertyDragEnd}
              isDragging={draggingOrder === property.order}
            />
          ))}
        </PropertyDropZone>
      </div>

      <button
        onClick={() => onCreateOffer(index)}
        className="w-full bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-2 rounded-md font-medium transition-colors"
      >
        Create Offer
      </button>
    </div>
  )
}

// One side (offering or offered) of the Trade Check panel: pick which
// configured player fills this role, then check/uncheck their properties and
// drag the money slider to include in the hypothetical trade.
const TradeCheckColumn: React.FC<{
  title: string
  accentClass: string
  players: IPlayer[]
  selectedIndex: number
  otherIndex: number
  onSelectPlayer: (index: number) => void
  playerProperties: PropertyData[]
  selectedProperties: number[]
  onToggleProperty: (order: number) => void
  money: number
  onMoneyChange: (money: number) => void
  moneyLabel: string
}> = ({
  title,
  accentClass,
  players,
  selectedIndex,
  otherIndex,
  onSelectPlayer,
  playerProperties,
  selectedProperties,
  onToggleProperty,
  money,
  onMoneyChange,
  moneyLabel,
}) => {
  const player = players[selectedIndex]
  return (
    <div className="bg-slate-950/40 rounded-lg p-3 border border-white/10">
      <div className="flex items-center justify-between mb-2.5">
        <label className={`text-xs font-semibold ${accentClass}`}>{title}</label>
        <select
          value={selectedIndex}
          onChange={(e) => onSelectPlayer(Number(e.target.value))}
          className="bg-white/20 text-white text-sm border border-white/30 rounded px-2 py-1 focus:outline-none focus:border-white"
        >
          {players.map((_, i) => (
            <option
              key={i}
              value={i}
              disabled={i === otherIndex}
              className="bg-gray-800 text-white"
            >
              Player {i + 1}
            </option>
          ))}
        </select>
      </div>

      <div className="text-white/60 text-xs mb-2">
        Money available:{" "}
        <span className="text-white font-medium">
          ${player?.money.toLocaleString() ?? 0}
        </span>
      </div>

      <div className="mb-3">
        <label className="text-white/70 text-[11px] font-medium block mb-1">
          Properties ({selectedProperties.length}/{playerProperties.length} selected)
        </label>
        <div className="min-h-9 rounded-lg p-1.5 border-2 border-dashed border-white/20 bg-white/5">
          <div className="flex flex-wrap gap-1">
            {playerProperties.map((property) => (
              <SelectablePropertyCard
                key={property.order}
                property={property}
                checked={selectedProperties.includes(property.order)}
                onToggle={onToggleProperty}
              />
            ))}
          </div>
          {playerProperties.length === 0 && (
            <div className="text-white/40 text-center py-1 text-[11px]">
              No properties
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-white/70 text-[11px] font-medium">
            {moneyLabel}
          </label>
          <span className="text-white text-xs font-semibold">${money}</span>
        </div>
        <input
          type="range"
          min={0}
          max={player?.money ?? 0}
          step={25}
          value={money}
          onChange={(e) => onMoneyChange(Number(e.target.value))}
          className="w-full accent-emerald-400"
        />
      </div>
    </div>
  )
}

const TradeSimulator: React.FC<TradeSimulatorProps> = () => {
  const [numPlayers, setNumPlayers] = useState(2)
  const [players, setPlayers] = useState<IPlayer[]>(() => {
    // Initialize with default players
    const defaultPlayers: IPlayer[] = []
    for (let i = 0; i < 2; i++) {
      defaultPlayers.push(createDefaultPlayer(i as PlayerOrder))
    }
    return defaultPlayers
  })

  const [bankProperties, setBankProperties] = useState<PropertyData[]>([])
  const [draggingOrder, setDraggingOrder] = useState<number | null>(null)
  const [dragOverTarget, setDragOverTarget] = useState<DropTarget | null>(null)
  // Result of the most recent "Create Offer" action, shown in a persistent
  // side panel instead of a transient alert.
  const [offerResult, setOfferResult] = useState<{
    type: "success" | "error" | "info"
    title: string
    data?: unknown
  } | null>(null)

  // Trade Check panel: lets you build a hypothetical trade between two of the
  // configured players and see whether the offered player (as a bot) would
  // accept it, via the dollar-based valuation (getTradeValueForPlayer).
  const [tradeOfferingIndex, setTradeOfferingIndex] = useState(0)
  const [tradeOfferedIndex, setTradeOfferedIndex] = useState(1)
  const [tradeGivenProperties, setTradeGivenProperties] = useState<number[]>([])
  const [tradeTakenProperties, setTradeTakenProperties] = useState<number[]>([])
  const [tradeMoneyGiven, setTradeMoneyGiven] = useState(0)
  const [tradeMoneyTaken, setTradeMoneyTaken] = useState(0)
  const [tradeCheckLoading, setTradeCheckLoading] = useState(false)
  const [tradeCheckError, setTradeCheckError] = useState<string | null>(null)
  const [tradeCheckResult, setTradeCheckResult] = useState<{
    willAccept: boolean
    fairness: number
    valuation?: TradeValuation
  } | null>(null)

  // Get all available properties from the default map
  const allProperties: PropertyData[] = React.useMemo(() => {
    const defaultMap = mapDesigns.find((map: any) => map.id === MAP_ID)
    return (
      defaultMap?.cells
        .filter((cell: any) => cell.type === "property")
        .map((cell: any) => {
          const groupId = cell.propertyDetails?.groupId
          const group = defaultMap?.groups?.find((g: any) => g.id === groupId)
          return {
            order: cell.order,
            name: cell.propertyDetails?.name || `Property ${cell.order}`,
            price: cell.propertyDetails?.price || 0,
            color: group?.color || "#CCCCCC",
            groupId: groupId,
          }
        }) || []
    )
  }, [])

  // Order in which color groups should appear (matches the map's group list,
  // falling back to first-appearance order for anything not in it).
  const groupOrder: string[] = React.useMemo(() => {
    const defaultMap = mapDesigns.find((map: any) => map.id === MAP_ID)
    const order: string[] = []
    const seen = new Set<string>()
    ;(defaultMap?.groups || []).forEach((g: any) => {
      if (!seen.has(g.id)) {
        order.push(g.id)
        seen.add(g.id)
      }
    })
    allProperties.forEach((p) => {
      const groupId = p.groupId || "__none__"
      if (!seen.has(groupId)) {
        order.push(groupId)
        seen.add(groupId)
      }
    })
    return order
  }, [allProperties])

  // Bank properties grouped by color group, one group per row.
  const bankGroups: PropertyData[][] = React.useMemo(() => {
    const byGroup = new Map<string, PropertyData[]>()
    bankProperties.forEach((p) => {
      const groupId = p.groupId || "__none__"
      if (!byGroup.has(groupId)) byGroup.set(groupId, [])
      byGroup.get(groupId)!.push(p)
    })
    return groupOrder
      .map((groupId) => byGroup.get(groupId))
      .filter((group): group is PropertyData[] => !!group && group.length > 0)
  }, [bankProperties, groupOrder])

  // Initialize bank properties only once
  React.useEffect(() => {
    setBankProperties(allProperties)
  }, [allProperties])

  // Load from localStorage on mount
  React.useEffect(() => {
    const savedPlayers = localStorage.getItem(`tradeSimPlayers_${MAP_ID}`)
    const savedBank = localStorage.getItem(`tradeSimBankProperties_${MAP_ID}`)
    const savedNumPlayers = localStorage.getItem(`tradeSimNumPlayers_${MAP_ID}`)
    if (savedPlayers && savedBank && savedNumPlayers) {
      setPlayers(JSON.parse(savedPlayers))
      setBankProperties(JSON.parse(savedBank))
      setNumPlayers(Number(savedNumPlayers))
    }
  }, [])

  // Save to localStorage on changes
  React.useEffect(() => {
    localStorage.setItem(`tradeSimPlayers_${MAP_ID}`, JSON.stringify(players))
    localStorage.setItem(
      `tradeSimBankProperties_${MAP_ID}`,
      JSON.stringify(bankProperties)
    )
    localStorage.setItem(`tradeSimNumPlayers_${MAP_ID}`, String(numPlayers))
  }, [players, bankProperties, numPlayers])

  // Keep the trade-check player indices in range as the player count changes,
  // and make sure the two roles never point at the same player.
  React.useEffect(() => {
    setTradeOfferingIndex((prev) => Math.min(prev, players.length - 1))
    setTradeOfferedIndex((prev) => Math.min(prev, players.length - 1))
  }, [players.length])

  React.useEffect(() => {
    if (tradeOfferedIndex === tradeOfferingIndex && players.length > 1) {
      const alt = players.findIndex((_, i) => i !== tradeOfferingIndex)
      if (alt !== -1) setTradeOfferedIndex(alt)
    }
  }, [tradeOfferingIndex, tradeOfferedIndex, players.length])

  // Switching which player fills a role clears that side's selections outright.
  React.useEffect(() => {
    setTradeGivenProperties([])
    setTradeMoneyGiven(0)
  }, [tradeOfferingIndex])

  React.useEffect(() => {
    setTradeTakenProperties([])
    setTradeMoneyTaken(0)
  }, [tradeOfferedIndex])

  // If the selected player's properties/money are edited elsewhere in the
  // simulator, drop any selections that no longer apply instead of resetting.
  React.useEffect(() => {
    const offeringPlayer = players[tradeOfferingIndex]
    const ownedOrders = new Set(offeringPlayer?.properties ?? [])
    setTradeGivenProperties((prev) => prev.filter((order) => ownedOrders.has(order)))
    setTradeMoneyGiven((prev) => Math.min(prev, offeringPlayer?.money ?? 0))
  }, [players, tradeOfferingIndex])

  React.useEffect(() => {
    const offeredPlayer = players[tradeOfferedIndex]
    const ownedOrders = new Set(offeredPlayer?.properties ?? [])
    setTradeTakenProperties((prev) => prev.filter((order) => ownedOrders.has(order)))
    setTradeMoneyTaken((prev) => Math.min(prev, offeredPlayer?.money ?? 0))
  }, [players, tradeOfferedIndex])

  // Clear a stale check result whenever any part of the trade changes.
  React.useEffect(() => {
    setTradeCheckResult(null)
    setTradeCheckError(null)
  }, [
    tradeOfferingIndex,
    tradeOfferedIndex,
    tradeGivenProperties,
    tradeTakenProperties,
    tradeMoneyGiven,
    tradeMoneyTaken,
  ])

  function createDefaultPlayer(order: PlayerOrder): IPlayer {
    return {
      money: 1500,
      savedFromRent: false,
      properties: [],
      pieceId: `piece-${order}`,
      position: 0,
      eliminated: false,
      rollHistory: [],
      id: `player-${order}`,
      order: order,
      inJail: false,
      inJailTurns: 0,
      jailFreeCards: [],
      isBankrupt: false,
      boughtPropertiesWithoutAuction: 0,
      canBuyPropertiesWithoutAuction: 0,
      cards: [],
      stock: 0,
      role: "none" as GameRoleId,
      stockMultiplier: 1,
      turnsBeingBankrupt: 0,
      bot: false,
    }
  }

  const handleNumPlayersChange = (newNum: number) => {
    setNumPlayers(newNum)

    // Update players array
    const newPlayers: IPlayer[] = []
    for (let i = 0; i < newNum; i++) {
      if (i < players.length) {
        newPlayers.push(players[i])
      } else {
        newPlayers.push(createDefaultPlayer(i as PlayerOrder))
      }
    }
    setPlayers(newPlayers)
  }

  const updatePlayerMoney = (playerIndex: number, money: number) => {
    const newPlayers = [...players]
    newPlayers[playerIndex] = { ...newPlayers[playerIndex], money }
    setPlayers(newPlayers)
  }

  const updatePlayerField = (
    playerIndex: number,
    field: keyof IPlayer,
    value: any
  ) => {
    const newPlayers = [...players]
    newPlayers[playerIndex] = { ...newPlayers[playerIndex], [field]: value }
    setPlayers(newPlayers)
  }

  const handlePropertyDragStart = (
    event: React.DragEvent,
    property: PropertyData
  ) => {
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", String(property.order))
    setDraggingOrder(property.order)
  }

  const handlePropertyDragEnd = () => {
    setDraggingOrder(null)
    setDragOverTarget(null)
  }

  // Moves a property (by order) to the given target, removing it from
  // wherever it currently lives (bank or another player).
  const moveProperty = (propertyOrder: number, target: DropTarget) => {
    const property = allProperties.find((p) => p.order === propertyOrder)
    if (!property) return

    const currentOwnerIndex = players.findIndex((p) =>
      p.properties.includes(propertyOrder)
    )

    if (target === "bank") {
      if (currentOwnerIndex === -1) return // already in the bank

      const newPlayers = [...players]
      newPlayers[currentOwnerIndex] = {
        ...newPlayers[currentOwnerIndex],
        properties: newPlayers[currentOwnerIndex].properties.filter(
          (p) => p !== propertyOrder
        ),
      }
      setPlayers(newPlayers)
      setBankProperties((prev) =>
        prev.some((p) => p.order === propertyOrder) ? prev : [...prev, property]
      )
      return
    }

    if (currentOwnerIndex === target) return // no-op, already owned by this player

    const newPlayers = [...players]
    if (currentOwnerIndex !== -1) {
      newPlayers[currentOwnerIndex] = {
        ...newPlayers[currentOwnerIndex],
        properties: newPlayers[currentOwnerIndex].properties.filter(
          (p) => p !== propertyOrder
        ),
      }
    }
    newPlayers[target] = {
      ...newPlayers[target],
      properties: [...newPlayers[target].properties, propertyOrder],
    }
    setPlayers(newPlayers)
    setBankProperties((prev) => prev.filter((p) => p.order !== propertyOrder))
  }

  const handleDrop = (target: DropTarget, event: React.DragEvent) => {
    const orderStr = event.dataTransfer.getData("text/plain")
    const order = Number(orderStr)
    setDragOverTarget(null)
    setDraggingOrder(null)
    if (!Number.isFinite(order)) return
    moveProperty(order, target)
  }

  const removeAllPlayerProperties = (playerIndex: number) => {
    const player = players[playerIndex]
    if (!player || player.properties.length === 0) return

    const propsToReturn = allProperties.filter((p) =>
      player.properties.includes(p.order)
    )

    const newPlayers = [...players]
    newPlayers[playerIndex] = { ...player, properties: [] }
    setPlayers(newPlayers)

    setBankProperties((prev) => {
      const existingOrders = new Set(prev.map((p) => p.order))
      const toAdd = propsToReturn.filter((p) => !existingOrders.has(p.order))
      return [...prev, ...toAdd]
    })
  }

  const resetToDefaults = () => {
    const defaultPlayers: IPlayer[] = []
    for (let i = 0; i < numPlayers; i++) {
      defaultPlayers.push(createDefaultPlayer(i as PlayerOrder))
    }
    setPlayers(defaultPlayers)
    setBankProperties(allProperties)
    setDraggingOrder(null)
    setDragOverTarget(null)
  }

  const getPlayerProperties = (playerIndex: number): PropertyData[] => {
    const player = players[playerIndex]
    return allProperties.filter((property) =>
      player.properties.includes(property.order)
    )
  }

  // Trade Check panel handlers. Selecting the same player for both roles
  // isn't allowed, so picking one that's already taken swaps the other role
  // onto whoever was previously selected.
  const handleTradeOfferingSelect = (index: number) => {
    setTradeOfferingIndex(index)
    if (index === tradeOfferedIndex) {
      setTradeOfferedIndex(tradeOfferingIndex)
    }
  }

  const handleTradeOfferedSelect = (index: number) => {
    setTradeOfferedIndex(index)
    if (index === tradeOfferingIndex) {
      setTradeOfferingIndex(tradeOfferedIndex)
    }
  }

  const toggleTradeGivenProperty = (order: number) => {
    setTradeGivenProperties((prev) =>
      prev.includes(order) ? prev.filter((o) => o !== order) : [...prev, order]
    )
  }

  const toggleTradeTakenProperty = (order: number) => {
    setTradeTakenProperties((prev) =>
      prev.includes(order) ? prev.filter((o) => o !== order) : [...prev, order]
    )
  }

  // Only one side's money can be in the trade at a time — setting one resets
  // the other, mirroring how a real trade offer works (give money OR take it).
  const handleTradeMoneyGivenChange = (money: number) => {
    setTradeMoneyGiven(money)
    setTradeMoneyTaken(0)
  }

  const handleTradeMoneyTakenChange = (money: number) => {
    setTradeMoneyTaken(money)
    setTradeMoneyGiven(0)
  }

  const handleCheckTrade = async () => {
    setTradeCheckLoading(true)
    setTradeCheckError(null)
    setTradeCheckResult(null)
    try {
      const gameData = buildGameData(tradeOfferingIndex)

      const gameResponse = await fetch(`${host}/api/trade/create-dummy-game`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ game: gameData }),
      })
      const gameResult = await gameResponse.json()
      if (!gameResult.success) {
        setTradeCheckError("Failed to sync game state")
        return
      }

      const trade: ITradeSimTrade = {
        offeringPlayerOrder: players[tradeOfferingIndex].order,
        offeredPlayerOrder: players[tradeOfferedIndex].order,
        givenProperties: tradeGivenProperties,
        takenProperties: tradeTakenProperties,
        moneyGiven: tradeMoneyGiven,
        moneyTaken: tradeMoneyTaken,
      }

      const response = await fetch(`${host}/api/trade/check-trade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gameId: gameData.id, trade }),
      })
      const result = await response.json()

      if (result.success) {
        setTradeCheckResult({
          willAccept: result.willAccept,
          fairness: result.fairness,
          valuation: result.valuation,
        })
      } else {
        setTradeCheckError(result.message || "Failed to check trade")
      }
    } catch (error) {
      setTradeCheckError(error instanceof Error ? error.message : String(error))
    } finally {
      setTradeCheckLoading(false)
    }
  }

  // Builds dummy game data (in the shape the backend expects) from the
  // current players configuration. `turnPlayerIndex` just sets whose turn it
  // is in the situation, which the bot trade logic doesn't rely on but the
  // game shape expects.
  const buildGameData = (turnPlayerIndex: number) => {
    return {
      players: players.map((p, index) => `dummy-player-${index}@example.com`),
      host: `dummy-player-${turnPlayerIndex}@example.com`,
      id: `dummy-game-${Date.now()}`,
      hasStarted: true,
      settings: {
        maxPlayers: numPlayers,
        mapId: MAP_ID, // Mini map
        minPlayers: 2,
        noOfPlayers: numPlayers,
        maxTurns: 100,
        startingFunds: 1500,
        title: `Trade Simulator Game - ${Date.now()}`,
        endGameCardsNeeded: 3,
        roles: {},
      },
      isPrivate: false,
      password: "",
      situation: {
        historyItems: [],
        players: players.map((p, index) => ({
          money: p.money,
          savedFromRent: false,
          properties: p.properties,
          pieceId: `piece-${p.order}`,
          position: 0,
          eliminated: false,
          rollHistory: [],
          id: `dummy-player-${index}@example.com`,
          order: p.order as PlayerOrder,
          inJail: false,
          inJailTurns: 0,
          jailFreeCards: [],
          isBankrupt: false,
          boughtPropertiesWithoutAuction: 0,
          canBuyPropertiesWithoutAuction: 0,
          cards: [],
          stock: p.stock,
          role: p.role,
          stockMultiplier: p.stockMultiplier,
          turnsBeingBankrupt: 0,
          bot: false,
        })),
        turnsLeft: 100,
        turn: `dummy-player-${turnPlayerIndex}@example.com`,
        rollEnabled: true,
        endTurnEnabled: true,
        chanceCardsOrder: [],
        communityChestCardOrder: [],
        recentlyDrawnChanceId: "",
        recentlyDrawnCommunityChestId: "",
        activeAuctionPropertyOrder: null,
        events: [],
        mortgagedProperties: [],
        housesData: [],
        endGameCardsDrawn: 0,
        gameOver: false,
        constructionBanned: false,
        turnRemainingForConstructionBan: 0,
        pickingRoles: false,
        pickedRoles: {},
        moneyOnTable: 0,
        cardsInDeck: {},
      },
    }
  }

  const handleCreateOffer = async (playerIndex: number) => {
    try {
      const player = players[playerIndex]
      const playerProperties = getPlayerProperties(playerIndex)
      const playerId = `dummy-player-${playerIndex}@example.com`
      // Create dummy game data with default values
      const gameData = buildGameData(playerIndex)

      // First, create the dummy game
      const gameResponse = await fetch(`${host}/api/trade/create-dummy-game`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ game: gameData }),
      })

      const gameResult = await gameResponse.json()

      if (!gameResult.success) {
        console.error("Failed to create dummy game:", gameResult)
        setOfferResult({
          type: "error",
          title: "Failed to create dummy game",
          data: gameResult,
        })
        return
      }

      // Then create the offer
      const offerData = {
        gameId: gameData.id,
        playerId,
      }

      const response = await fetch(`${host}/api/trade/create-offers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(offerData),
      })

      const result = await response.json()

      if (result.success) {
        if (result.tradeOffer) {
          setOfferResult({
            type: "success",
            title: `Trade offer generated for Player ${playerIndex + 1}`,
            data: result.tradeOffer,
          })
        } else {
          setOfferResult({
            type: "info",
            title: `No trade offer available for Player ${playerIndex + 1}`,
          })
        }
      } else {
        console.error("Failed to create offer:", result)
        setOfferResult({
          type: "error",
          title: "Failed to create offer",
          data: result,
        })
      }
    } catch (error) {
      console.error("Error creating offer:", error)
      setOfferResult({
        type: "error",
        title: "Error creating offer",
        data: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Split players across the two side columns, bank sits in the middle.
  const halfIndex = Math.ceil(players.length / 2)
  const leftIndices = players.map((_, i) => i).slice(0, halfIndex)
  const rightIndices = players.map((_, i) => i).slice(halfIndex)

  const renderPlayerCard = (index: number) => {
    const player = players[index]
    const accent = PLAYER_ACCENTS[index % PLAYER_ACCENTS.length]
    return (
      <PlayerCard
        key={index}
        player={player}
        index={index}
        accent={accent}
        playerProperties={getPlayerProperties(index)}
        dragOverTarget={dragOverTarget}
        onDragEnter={setDragOverTarget}
        onDragLeave={() => setDragOverTarget(null)}
        onDrop={handleDrop}
        onPropertyDragStart={handlePropertyDragStart}
        onPropertyDragEnd={handlePropertyDragEnd}
        draggingOrder={draggingOrder}
        updatePlayerMoney={updatePlayerMoney}
        updatePlayerField={updatePlayerField}
        removeAllPlayerProperties={removeAllPlayerProperties}
        onCreateOffer={handleCreateOffer}
      />
    )
  }

  return (
    <div
      className="w-screen h-screen bg-cover bg-fixed overflow-auto"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Number of Players — fixed, compact, top-right */}
      <div className="fixed top-3 right-3 z-50 flex items-center gap-1.5 bg-slate-900/70 border border-white/10 rounded-lg px-2 py-1.5 shadow-lg">
        <span className="text-white/50 text-[10px] font-medium mr-0.5">
          Players
        </span>
        {[2, 3, 4, 5, 6].map((num) => (
          <button
            key={num}
            onClick={() => handleNumPlayersChange(num)}
            className={`
              w-6 h-6 rounded text-xs font-medium transition-colors border
              ${
                numPlayers === num
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-white/10 border-white/20 text-white/70 hover:bg-white/20 hover:text-white"
              }
            `}
          >
            {num}
          </button>
        ))}
      </div>

      <div className="p-2.5">
        <div className="max-w-7xl mx-auto">
          {/* Main layout: players | bank | players */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2.5 items-start">
            <div className="flex flex-col gap-3">
              {leftIndices.map(renderPlayerCard)}
            </div>

            {/* Bank Properties Column */}
            <div className="w-64 bg-slate-900/60 rounded-lg p-2 border border-white/10 shadow-lg">
              <div className="mb-1.5">
                <h2 className="text-xs font-semibold text-white">
                  🏦 Bank ({bankProperties.length})
                </h2>
                <span className="text-white/40 text-[10px]">
                  $
                  {bankProperties
                    .reduce((sum, p) => sum + p.price, 0)
                    .toLocaleString()}{" "}
                  value
                </span>
              </div>
              <PropertyDropZone
                target="bank"
                isActive={dragOverTarget === "bank"}
                onDragEnter={setDragOverTarget}
                onDragLeave={() => setDragOverTarget(null)}
                onDrop={handleDrop}
                isEmpty={bankProperties.length === 0}
                emptyLabel="Empty"
                layout="rows"
              >
                {bankGroups.map((group) => (
                  <div
                    key={group[0].groupId || group[0].order}
                    className="flex flex-wrap gap-1"
                  >
                    {group.map((property) => (
                      <PropertySmallCard
                        key={property.order}
                        property={property}
                        isInBank
                        onDragStart={handlePropertyDragStart}
                        onDragEnd={handlePropertyDragEnd}
                        isDragging={draggingOrder === property.order}
                      />
                    ))}
                  </div>
                ))}
              </PropertyDropZone>

              {/* Action buttons live under the bank column to save vertical space */}
              <div className="flex flex-col gap-1 mt-2">
                <button
                  onClick={resetToDefaults}
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-[11px] px-2 py-1 rounded-md font-medium transition-colors"
                >
                  Reset to Defaults
                </button>
                <button
                  onClick={() => {
                    setOfferResult({
                      type: "info",
                      title: "Current Players Configuration",
                      data: players,
                    })
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-[11px] px-2 py-1 rounded-md font-medium transition-colors"
                >
                  Export Configuration
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {rightIndices.map(renderPlayerCard)}
            </div>
          </div>

          {/* Trade Check panel */}
          <div className="mt-4 bg-slate-900/60 rounded-lg p-4 border border-white/10 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white text-base font-semibold">
                🤝 Trade Check (Bot Decision)
              </h2>
              <span className="text-white/40 text-[10px]">
                Uses getTradeValueForPlayer from bot/trade.ts
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TradeCheckColumn
                title="Offering Player"
                accentClass="text-blue-300"
                players={players}
                selectedIndex={tradeOfferingIndex}
                otherIndex={tradeOfferedIndex}
                onSelectPlayer={handleTradeOfferingSelect}
                playerProperties={getPlayerProperties(tradeOfferingIndex)}
                selectedProperties={tradeGivenProperties}
                onToggleProperty={toggleTradeGivenProperty}
                money={tradeMoneyGiven}
                onMoneyChange={handleTradeMoneyGivenChange}
                moneyLabel="Money offered"
              />
              <TradeCheckColumn
                title="Offered Player (decides)"
                accentClass="text-purple-300"
                players={players}
                selectedIndex={tradeOfferedIndex}
                otherIndex={tradeOfferingIndex}
                onSelectPlayer={handleTradeOfferedSelect}
                playerProperties={getPlayerProperties(tradeOfferedIndex)}
                selectedProperties={tradeTakenProperties}
                onToggleProperty={toggleTradeTakenProperty}
                money={tradeMoneyTaken}
                onMoneyChange={handleTradeMoneyTakenChange}
                moneyLabel="Money requested"
              />
            </div>

            <button
              onClick={handleCheckTrade}
              disabled={tradeCheckLoading || players.length < 2}
              className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-white/10 disabled:text-white/30 disabled:cursor-not-allowed text-white text-sm px-3 py-2 rounded-md font-medium transition-colors"
            >
              {tradeCheckLoading ? "Checking..." : "Check Trade"}
            </button>

            {tradeCheckError && (
              <div className="mt-3 text-red-400 text-xs">{tradeCheckError}</div>
            )}

            {tradeCheckResult && (
              <div
                className={`mt-3 rounded-lg p-3 border ${
                  tradeCheckResult.willAccept
                    ? "bg-emerald-500/10 border-emerald-400/40"
                    : "bg-red-500/10 border-red-400/40"
                }`}
              >
                <div
                  className={`text-sm font-bold ${
                    tradeCheckResult.willAccept ? "text-emerald-400" : "text-red-400"
                  }`}
                >
                  {tradeCheckResult.willAccept
                    ? "✅ Bot would ACCEPT this trade"
                    : "❌ Bot would REJECT this trade"}
                </div>
                {tradeCheckResult.valuation && (
                  <div className="text-xs mt-2 space-y-1">
                    <div className="text-white/80 font-medium">
                      Gets ${tradeCheckResult.valuation.valueReceived} vs gives $
                      {tradeCheckResult.valuation.valueGiven} → net $
                      {tradeCheckResult.valuation.netValue} (needs &gt; $
                      {tradeCheckResult.valuation.requiredSurplus})
                    </div>
                    {tradeCheckResult.valuation.rejectedByGuard && (
                      <div className="text-amber-400">
                        ⛔ Guard: trade would drop cash to $
                        {tradeCheckResult.valuation.cashAfterTrade}, below the $
                        {tradeCheckResult.valuation.safetyReserve} safety reserve
                      </div>
                    )}
                    {tradeCheckResult.valuation.receivedProperties.map((prop) => (
                      <div key={`recv-${prop.propertyOrder}`} className="text-white/60">
                        ⬅️ #{prop.propertyOrder}: ${prop.totalValue} (price $
                        {prop.intrinsicValue}
                        {prop.setPremium > 0 &&
                          ` + set premium $${prop.setPremium}${
                            prop.completesSet ? " — completes set!" : ""
                          }`}
                        {prop.houseValue > 0 && ` + houses $${prop.houseValue}`})
                      </div>
                    ))}
                    {tradeCheckResult.valuation.givenProperties.map((prop) => (
                      <div key={`give-${prop.propertyOrder}`} className="text-white/60">
                        ➡️ #{prop.propertyOrder}: ${prop.totalValue} (price $
                        {prop.intrinsicValue}
                        {prop.setPremium > 0 &&
                          ` + set loss $${prop.setPremium}${
                            prop.breaksCompletedSet ? " — breaks my set!" : ""
                          }`}
                        {prop.threatValue > 0 &&
                          ` + threat $${prop.threatValue} (completes their set)`}
                        {prop.houseValue > 0 && ` + houses $${prop.houseValue}`})
                      </div>
                    ))}
                    {tradeCheckResult.valuation.moneyReceived > 0 && (
                      <div className="text-white/60">
                        ⬅️ Cash ${tradeCheckResult.valuation.moneyReceived}
                        {tradeCheckResult.valuation.moneyReceivedUtility !==
                          tradeCheckResult.valuation.moneyReceived &&
                          ` (worth $${tradeCheckResult.valuation.moneyReceivedUtility} — low on cash)`}
                      </div>
                    )}
                    {tradeCheckResult.valuation.moneyGiven > 0 && (
                      <div className="text-white/60">
                        ➡️ Cash ${tradeCheckResult.valuation.moneyGiven} (cash after: $
                        {tradeCheckResult.valuation.cashAfterTrade}, reserve $
                        {tradeCheckResult.valuation.safetyReserve})
                      </div>
                    )}
                  </div>
                )}
                <div className="text-white/40 text-[10px] mt-1">
                  Legacy fairness score: {tradeCheckResult.fairness.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Persistent result panel (replaces alerts) */}
      <div className="fixed bottom-3 right-3 z-40 w-80 max-w-[90vw] max-h-[50vh] flex flex-col bg-gray-900/90 backdrop-blur-sm border border-white/20 rounded-lg shadow-xl text-white">
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <h3 className="text-sm font-semibold">Result</h3>
          {offerResult && (
            <button
              onClick={() => setOfferResult(null)}
              className="text-white/60 hover:text-white text-xs"
            >
              Clear
            </button>
          )}
        </div>
        <div className="p-3 overflow-auto">
          {offerResult ? (
            <>
              <div
                className={`text-xs font-medium mb-1.5 ${
                  offerResult.type === "success"
                    ? "text-green-400"
                    : offerResult.type === "error"
                    ? "text-red-400"
                    : "text-yellow-300"
                }`}
              >
                {offerResult.title}
              </div>
              {offerResult.data !== undefined && (
                <pre className="text-[11px] bg-black/40 rounded p-2 overflow-auto whitespace-pre-wrap break-words">
                  {typeof offerResult.data === "string"
                    ? offerResult.data
                    : JSON.stringify(offerResult.data, null, 2)}
                </pre>
              )}
            </>
          ) : (
            <div className="text-white/50 text-xs">
              Create an offer to see the result here.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TradeSimulator
